import { showError } from "./error";
import { DefaultThunkAction } from "./shared";
import { AnyAction } from "redux";
import { PlanType, WorkState } from "../reducers/models";
import actions from "../constants/actions.json";
import { getHouseAdCampaign, getSaleData } from "../utils/saleUtils";
import Log from "../../src/utils/Log";
import { getDiscountedPlanPricing } from "../utils/saleUtils";

// gets called in /plans and /purchase to check for any active sales (404 means no sale)
export function fetchSale(
  hasPreviousSale: boolean = false
): DefaultThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const cachedSaleData = state.sale?.saleData; 

    // If sale data already exists, avoid calling the API
    if (cachedSaleData && Object.keys(cachedSaleData).length > 0) {
      await dispatch(setSaleData(cachedSaleData));
      dispatch(setSaleUI(true));
      dispatch(showSalesLoader(false));
      const discountedPricing = await getDiscountedPlanPricing();
      await dispatch(discountedCost(discountedPricing));
      dispatch(setSaleWorkState(WorkState.None));
      return;
    }

    dispatch(setSaleWorkState(WorkState.Loading));
    dispatch(showSalesLoader(true));

    try {
      let saleData: bb.model.sale.ISaleData|null = await getSaleData();

      if (saleData) {
        // we are treating bundle plans as special case for now
        // this chunk of code just looks for a single bundle-plan discount info
        // and renames it to "Bundle" if found
        const bundleDiscountObj = saleData.discountInfo.BBBundle
        if (bundleDiscountObj !== undefined) {
          const bundlePlanDiscounts = Object.values(bundleDiscountObj)
          if (bundlePlanDiscounts.length > 1) {
            Log.error(`multiple bundle plan discounts found! We only know how to deal with one kind of bundle plan right now!`)
            Log.error(Object.keys(bundleDiscountObj).join(","))
            throw new Error("Unable to process sale info.")
          }
          saleData.discountInfo.BBBundle = {[PlanType.Bundle]: bundlePlanDiscounts[0]}
        }
        await dispatch(setSaleData(saleData));
        dispatch(setSaleUI(true));
        dispatch(showSalesLoader(false));

        const discountedPricing = await getDiscountedPlanPricing();

        await dispatch(discountedCost(discountedPricing));
      } 
      else {
        dispatch(resetSaleState());

        if (hasPreviousSale) {
          dispatch(saleJustEnded(true));
        }
      }

      dispatch(setSaleWorkState(WorkState.None));
    } catch (e:any) {
      //displays a non-404 error as a toast
      Log.error("fetchSale dispatch error", e);
      console.log('fetchSale dispatch error', e)
      dispatch(showError(e.message));
      dispatch(showSalesLoader(false));
      dispatch(resetSaleState());
      dispatch(setSaleWorkState(WorkState.Error));
    }
  };
}

export function fetchHouseAdCampaign(placement: keyof bb.state.ISale["houseAdCampaign"]): DefaultThunkAction<Promise<void>> {
  return async function (dispatch) {
    const houseAdCampaign =  await getHouseAdCampaign(placement)

    // FUTURE OPTIMIZATION: the SET function could maybe be generalized to do this check for us - or a lookup table used
    if (placement === "website_homepage") {
      dispatch(setHouseAdCampaignHomepage(houseAdCampaign))
    }
    else if (placement === "website_pricing") {
      dispatch(setHouseAdCampaignPricing(houseAdCampaign))
    }
    else if (placement === "website_stripe") {
      dispatch(setHouseAdCampaignStripe(houseAdCampaign))
    }
    else {
      Log.error(`something is broken! after fetching house ad campaign - unknown placement key not caught by tsc-compiler: ${placement}`)
      // silently fail aside from the error log above - not important enough to cause issues elsewhere in the site.
    }
  }
}

export function resetSaleState(): DefaultThunkAction<Promise<void>> {
  return async (dispatch) => {
    Log.trace("", "clearing sale state");
    sessionStorage.removeItem("promoCode");
    window.history.replaceState(null, window.location.pathname);

    dispatch(clearSaleState());
  };
}

export function setSaleWorkState(saleState: WorkState): AnyAction {
  return {
    type: actions.sale.SET_SALE_STATE,
    saleState,
  };
}

export function setSaleData(saleData: bb.model.sale.ISaleData): AnyAction {
  return {
    type: actions.sale.SET_SALE_DATA,
    saleData,
  };
}

export function setSaleUI(showSaleUI: boolean): AnyAction {
  return {
    type: actions.sale.SET_SALE_UI,
    showSaleUI,
  };
}

export function showSalesLoader(showSalesLoader: boolean): AnyAction {
  return {
    type: actions.sale.SET_SALE_LOADER,
    showSalesLoader,
  };
}

export function discountedCost(discountedCost: bb.model.PlanJSON): AnyAction {
  return {
    type: actions.sale.SET_DISCOUNTED_COST,
    discountedCost,
  };
}

export function setTotalDiscountedCost(totalCost: number): AnyAction {
  return {
    type: actions.sale.SET_TOTAL_COST,
    totalCost,
  };
}

export function saleJustEnded(saleJustEnded: boolean): AnyAction {
  return {
    type: actions.sale.SET_SALE_JUST_ENDED,
    saleJustEnded,
  };
}

export function clearSaleState(): AnyAction {
  return {
    type: actions.sale.RESET_SALE_STATE,
  };
}

export function clearSaleData(): AnyAction {
  return {
    type: actions.sale.CLEAR_SALE_DATA,
  };
}

export function setHouseAdCampaignHomepage(houseAdCampaign: bb.model.sale.IHouseAdCampaign|null): AnyAction {
  return {
    type: actions.sale.SET_HOUSE_AD_CAMPAIGN_HOMEPAGE,
    houseAdCampaign
  }
}
export function setHouseAdCampaignPricing(houseAdCampaign: bb.model.sale.IHouseAdCampaign|null): AnyAction {
  return {
    type: actions.sale.SET_HOUSE_AD_CAMPAIGN_PRICING,
    houseAdCampaign
  }
}
export function setHouseAdCampaignStripe(houseAdCampaign: bb.model.sale.IHouseAdCampaign|null): AnyAction {
  return {
    type: actions.sale.SET_HOUSE_AD_CAMPAIGN_STRIPE,
    houseAdCampaign
  }
}
